import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, ShoppingCart, Package, TrendingUp, AlertCircle, RefreshCw, X } from 'lucide-react';
import { reportsAPI, productsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { formatCurrency, formatNumber } from '../../utils/currency';
import { formatTableDate } from '../../utils/dateFormatter';

export default function Dashboard() {
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [stockFilter, setStockFilter] = useState(null); // null | 'low' | 'out'
  const [stockProducts, setStockProducts] = useState([]);
  const [loadingStock, setLoadingStock]   = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getDashboard();
      setData(response.data.dashboard);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClick = async (type) => {
    // Toggle off if same filter clicked again
    if (stockFilter === type) {
      setStockFilter(null);
      setStockProducts([]);
      return;
    }

    try {
      setLoadingStock(true);
      setStockFilter(type);
      const response = await productsAPI.getAll();
      const all = response.data.products;

      const filtered = type === 'out'
        ? all.filter(p => p.inventory_quantity === 0)
        : all.filter(p => p.inventory_quantity > 0 && p.inventory_quantity <= 10);

      setStockProducts(filtered);
    } catch (error) {
      toast.error('Failed to load stock data');
    } finally {
      setLoadingStock(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }

  const stats = [
    {
      title: "Today's Sales",
      value: formatCurrency(data?.today?.sales_today),
      icon: DollarSign,
      color: 'bg-gradient-to-br from-blue-500 to-blue-600',
      textColor: 'text-blue-600'
    },
    {
      title: "Today's Profit",
      value: formatCurrency(data?.today?.profit_today),
      icon: TrendingUp,
      color: 'bg-gradient-to-br from-green-500 to-green-600',
      textColor: 'text-green-600'
    },
    {
      title: 'Orders Today',
      value: formatNumber(data?.today?.orders_today),
      icon: ShoppingCart,
      color: 'bg-gradient-to-br from-purple-500 to-purple-600',
      textColor: 'text-purple-600'
    },
    {
      title: 'Total Products',
      value: formatNumber(data?.inventory?.total_products),
      icon: Package,
      color: 'bg-gradient-to-br from-orange-500 to-orange-600',
      textColor: 'text-orange-600'
    }
  ];

  return (
    <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">Dashboard</h1>
        <p className="text-gray-600">Welcome to your Shopify Management System</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden transform hover:scale-105 transition-transform duration-200">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <stat.icon className="text-white" size={24} />
                </div>
              </div>
              <h3 className="text-gray-500 text-sm font-medium mb-1">{stat.title}</h3>
              <p className={`text-3xl font-bold ${stat.textColor}`}>{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Inventory Alerts — clickable */}
      {(data?.inventory?.low_stock > 0 || data?.inventory?.out_of_stock > 0) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg">
          <div className="flex items-start">
            <AlertCircle className="text-yellow-600 mr-3 mt-0.5 flex-shrink-0" size={24} />
            <div className="flex-1">
              <h3 className="text-yellow-800 font-semibold mb-2">Inventory Alerts</h3>
              <div className="flex flex-wrap gap-3">
                {data?.inventory?.low_stock > 0 && (
                  <button
                    onClick={() => handleAlertClick('low')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
                      stockFilter === 'low'
                        ? 'bg-yellow-500 text-white border-yellow-500'
                        : 'bg-white text-yellow-700 border-yellow-400 hover:bg-yellow-100'
                    }`}
                  >
                    ⚠️ {formatNumber(data.inventory.low_stock)} low in stock
                    {stockFilter === 'low' ? ' — click to hide' : ' — click to view'}
                  </button>
                )}
                {data?.inventory?.out_of_stock > 0 && (
                  <button
                    onClick={() => handleAlertClick('out')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
                      stockFilter === 'out'
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-red-700 border-red-400 hover:bg-red-50'
                    }`}
                  >
                    🚫 {formatNumber(data.inventory.out_of_stock)} out of stock
                    {stockFilter === 'out' ? ' — click to hide' : ' — click to view'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stock Products Panel */}
      {stockFilter && (
        <div className={`mb-8 bg-white rounded-xl shadow-lg overflow-hidden border-t-4 ${
          stockFilter === 'out' ? 'border-red-400' : 'border-yellow-400'
        }`}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className={`text-lg font-bold ${stockFilter === 'out' ? 'text-red-700' : 'text-yellow-700'}`}>
              {stockFilter === 'out' ? '🚫 Out of Stock Products' : '⚠️ Low Stock Products (≤10 units)'}
            </h2>
            <button
              onClick={() => { setStockFilter(null); setStockProducts([]); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          {loadingStock ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="animate-spin text-gray-400" size={28} />
            </div>
          ) : stockProducts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No products found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">Product</th>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">SKU</th>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">Variant</th>
                    <th className="text-right py-3 px-6 text-gray-600 font-semibold">Price</th>
                    <th className="text-right py-3 px-6 text-gray-600 font-semibold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {stockProducts.map((product, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-6 font-medium text-gray-800">{product.title}</td>
                      <td className="py-3 px-6 text-gray-600">{product.sku || 'N/A'}</td>
                      <td className="py-3 px-6 text-gray-500 text-sm">
                        {product.variant_title && product.variant_title !== 'Default Title'
                          ? product.variant_title : '—'}
                      </td>
                      <td className="py-3 px-6 text-right font-semibold text-blue-600">
                        {formatCurrency(product.price)}
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          product.inventory_quantity === 0
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {product.inventory_quantity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500 border-t border-gray-100">
                Showing {stockProducts.length} product{stockProducts.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Monthly Overview */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">This Month</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <span className="text-gray-700 font-medium">Total Orders</span>
              <span className="text-2xl font-bold text-blue-600">
                {formatNumber(data?.month?.orders_month)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-gray-700 font-medium">Total Sales</span>
              <span className="text-2xl font-bold text-green-600">
                {formatCurrency(data?.month?.sales_month)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
              <span className="text-gray-700 font-medium">Total Profit</span>
              <span className="text-2xl font-bold text-purple-600">
                {formatCurrency(data?.month?.profit_month)}
              </span>
            </div>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Top Products by Profit</h2>
          <div className="space-y-3">
            {data?.top_products && data.top_products.length > 0 ? (
              data.top_products.slice(0, 5).map((product, index) => (
                <div key={index} className="flex justify-between items-center gap-4 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{product.product_title}</p>
                    <p className="text-sm text-gray-500">{formatNumber(product.units_sold)} units sold</p>
                  </div>
                  <span className="font-bold text-green-600 whitespace-nowrap flex-shrink-0">
                    {formatCurrency(product.total_profit)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No products data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recent Orders</h2>
        <div className="overflow-x-auto">
          {data?.recent_orders && data.recent_orders.length > 0 ? (
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Order #</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Product</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Sales</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-semibold">Profit</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_orders.map((order, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 font-medium text-gray-800">{order.order_number}</td>
                    <td className="py-3 px-4 text-gray-700">{order.product_title}</td>
                    <td className="py-3 px-4 text-right font-semibold text-blue-600">
                      {formatCurrency(order.selling_price)}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-green-600">
                      {formatCurrency(order.net_profit)}
                    </td>
                    <td className="py-3 px-4 text-gray-600">{formatTableDate(order.order_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-gray-500 text-center py-4">No recent orders available</p>
          )}
        </div>
      </div>
    </div>
  );
}